import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionHeading from "@/components/feature/SectionHeading";
import usePageSeo from "@/hooks/usePageSeo";
import { stats, testimonials } from "@/mocks/gua";

const principles = [
  {
    icon: "ri-shield-check-line",
    title: "只整理公开话题",
    text: "一切内容来源于已公开的讨论与可查证的节点，不涉及个人隐私，不接受未经核实的指控类信息。",
  },
  {
    icon: "ri-scales-3-line",
    title: "区分确认与流传",
    text: "对无法核实的部分，我们会明确标注为「流传版本」，不与已确认信息混为一谈，尽量还原事情原貌。",
  },
  {
    icon: "ri-time-line",
    title: "按时间线呈现",
    text: "比起罗列碎片，我们更愿意把关键节点排成一条线，让读者自己看清来龙去脉。",
  },
  {
    icon: "ri-refresh-line",
    title: "长期维护合集",
    text: "合集不是发完就结束。新的公开节点出现时，我们会回到原合集继续补充，收藏之后随时可回看。",
  },
];

const milestones = [
  { year: "起点", text: "从整理第一批「考古旧瓜」开始，用时间线的方式重新讲旧话题。" },
  { year: "扩展", text: "增开「名场面补档」与「圈内合集」两条主线，把零散消息串成脉络。" },
  { year: "现在", text: "已收录数百个合集，持续维护中，读者可长期回看并看到后续补充。" },
];

export default function About() {
  usePageSeo({
    title: "关于我们 | 蝌蚪吃瓜的内容原则与定位",
    description:
      "了解蝌蚪吃瓜的品牌定位、发展理念与内容原则：只整理公开话题、区分已确认与流传版本、按时间线呈现、长期维护合集。",
    keywords: "蝌蚪吃瓜,关于我们,内容原则,品牌介绍,考古合集",
    canonical: "/about",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "AboutPage",
        name: "关于我们 - 蝌蚪吃瓜",
        url: "https://haeremai.cn/about",
        inLanguage: "zh-CN",
      },
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "蝌蚪吃瓜",
        url: "https://haeremai.cn",
        email: "hello@tadpolegua.com",
        description: "专注整理娱乐行业公开话题的内容聚合站，以合集、补档、考古旧瓜为主要内容形态。",
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://haeremai.cn/" },
          { "@type": "ListItem", position: 2, name: "关于我们", item: "https://haeremai.cn/about" },
        ],
      },
    ],
  });

  return (
    <div className="bg-background-50">
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-14">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
          <h1 className="mt-4 max-w-3xl font-heading text-2xl font-bold leading-snug text-foreground-950 md:text-4xl">
            我们把散落的瓜，整理成看得懂的合集
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            蝌蚪吃瓜是一个专注娱乐行业公开话题的内容整理站点。我们不做抢跑式播报，而是把已经发生的事按时间线重新梳理，
            让错过的人也能补上完整的一课。
          </p>
          <dl className="mt-8 grid grid-cols-2 gap-4 md:grid-cols-4">
            {stats.map((s) => (
              <div
                key={s.label}
                className="rounded-xl border border-background-200 bg-background-50 px-4 py-4"
              >
                <dt className="text-xs text-foreground-500">{s.label}</dt>
                <dd className="mt-1 font-heading text-xl font-bold text-primary-700 md:text-2xl">
                  {s.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-[1fr_420px] lg:items-center">
          <div>
            <SectionHeading
              eyebrow="品牌定位"
              title="一个认真吃瓜的地方"
              desc="很多人对「吃瓜」的印象停留在碎片与情绪，我们想提供另一种可能：把信息放回它原本的顺序里，读起来会清楚很多。"
            />
            <div className="mt-6 flex flex-col gap-4 text-sm leading-loose text-foreground-700 md:text-base">
              <p>
                站内的每一条内容，都会尽量标注时间节点、来源方向与信息状态。你会看到「已确认」「流传版本」这样的区分，
                这不是为了显得专业，而是希望你读完能自己判断，而不是被情绪牵着走。
              </p>
              <p>
                我们把内容分成「考古旧瓜」「名场面补档」「圈内合集」「实时热议」四条主线。前三条偏回溯与整理，
                最后一条负责跟住正在发生的讨论。它们共用同一套整理标准。
              </p>
            </div>
          </div>
          <div className="h-64 w-full overflow-hidden rounded-2xl bg-background-200 md:h-80">
            <img
              src="https://readdy.ai/api/search-image?query=Warm%20editorial%20illustration%20of%20an%20archivist%20desk%20with%20folders%2C%20timeline%20cards%20and%20a%20magnifier%2C%20muted%20olive%20and%20cream%20tones%2C%20grainy%20retro%20poster%20texture&width=900&height=760&seq=gua-about-hero&orientation=landscape"
              alt="整理档案与时间线的插画"
              title="蝌蚪吃瓜 - 内容整理方式"
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>
      </section>

      <section id="principles" className="bg-background-100 py-14 md:py-20">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <SectionHeading
            eyebrow="内容原则"
            title="四条我们一直在守的规矩"
            desc="它们决定了什么内容会被收录，以及以什么方式呈现。"
            align="center"
          />
          <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {principles.map((p, idx) => (
              <div
                key={p.title}
                className={`rounded-xl border p-6 ${
                  idx % 2 === 0
                    ? "border-background-200 bg-background-50"
                    : "border-accent-200 bg-accent-50"
                }`}
              >
                <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-primary-100 text-primary-700">
                  <i className={`${p.icon} text-xl`}></i>
                </span>
                <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">{p.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <SectionHeading eyebrow="发展理念" title="从一次考古开始的坚持" />
        <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
          {milestones.map((m) => (
            <div
              key={m.year}
              className="rounded-xl border border-background-200 bg-background-50 p-6"
            >
              <span className="inline-flex items-center gap-2 rounded-full bg-secondary-100 px-3 py-1 text-xs font-medium text-secondary-900">
                <i className="ri-bookmark-line"></i>
                {m.year}
              </span>
              <p className="mt-4 text-sm leading-relaxed text-foreground-700">{m.text}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-background-100 py-14 md:py-20">
        <div className="mx-auto max-w-[1280px] px-4 md:px-6">
          <SectionHeading title="读者口碑" align="center" />
          <div className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-3">
            {testimonials.map((t) => (
              <figure
                key={t.name}
                className="flex flex-col rounded-xl border border-background-200 bg-background-50 p-6"
              >
                <div className="flex items-center gap-1 text-secondary-500">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <i key={i} className="ri-star-fill text-sm"></i>
                  ))}
                </div>
                <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-700">
                  {t.text}
                </blockquote>
                <figcaption className="mt-4 border-t border-background-200 pt-3">
                  <span className="block text-sm font-medium text-foreground-900">{t.name}</span>
                  <span className="text-xs text-foreground-500">{t.role}</span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1280px] px-4 py-14 md:px-6 md:py-20">
        <div className="flex flex-col items-start gap-6 rounded-2xl border border-primary-200 bg-primary-50 p-8 md:flex-row md:items-center md:justify-between md:p-10">
          <div>
            <h2 className="font-heading text-xl font-bold text-foreground-950 md:text-2xl">
              有想法、有推荐，欢迎随时说
            </h2>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-foreground-600">
              无论是推荐一个值得考古的话题，还是对整理方式有建议，都可以通过留言告诉我们。
            </p>
          </div>
          <div className="flex flex-col gap-3 sm:flex-row">
            <Link
              to="/contact"
              className="flex items-center gap-2 rounded-md bg-primary-500 px-5 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 whitespace-nowrap"
            >
              <i className="ri-send-plane-line"></i>
              前往留言
            </Link>
            <a
              href="mailto:hello@tadpolegua.com"
              rel="nofollow"
              className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-5 py-3 text-sm text-foreground-800 transition-colors hover:border-primary-400 hover:text-primary-700 whitespace-nowrap"
            >
              <i className="ri-mail-line"></i>
              hello@tadpolegua.com
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}